// Parser for design system stories
// Format: stories are delimited by ">>> STORY: " at the start of a line
import { DesignSystemStory } from '../types';

function containsHtmlTags(code: string): boolean {
	// Check for HTML tags using a simple regex pattern
	// Matches opening tags like <div>, <p>, <span>, etc. or self-closing tags like <img />
	const htmlTagPattern = /<[a-z][\s\S]*?>/i;
	return htmlTagPattern.test(code);
}

// Parses stories from a custom format string.
// Format:
// >>> STORY: Title Here
// <code content here>
//
// >>> STORY: Another Title
// <more code>
//
export function parseStories(content: string | null | undefined): DesignSystemStory[] {
	if (!content || content.trim().length === 0) {
		return [];
	}

	const stories: DesignSystemStory[] = [];

	// Split by ">>> STORY: " pattern that is either at start of string or preceded by newline
	// This handles both cases:
	// 1. Content starting with ">>> STORY: " (no leading newline)
	// 2. Content with ">>> STORY: " after newline
	const sections = content.split(/(?:^|\n)>>> STORY: /);

	// Process each section
	for (let i = 0; i < sections.length; i++) {
		const section = sections[i].trim();

		// Skip empty sections (can happen if content starts with ">>> STORY: ")
		if (!section) {
			continue;
		}

		// Find the first newline to separate title from code
		const firstNewlineIndex = section.indexOf('\n');

		if (firstNewlineIndex === -1) {
			// No newline found - this section only has a title, no code
			// Skip it (stories need both title and code)
			continue;
		}

		const title = section.substring(0, firstNewlineIndex).trim();
		const code = section.substring(firstNewlineIndex + 1).trim();

		// Add story if both title and code are present
		// Mark it as valid/invalid HTML so the UI can handle broken stories appropriately
		if (title && code) {
			stories.push({
				title,
				code,
				isValidHtml: containsHtmlTags(code),
			});
		}
	}

	return stories;
}
